import React, {useEffect, useMemo, useRef, useState} from 'react'
import * as d3 from 'd3'
import moment from 'moment'
import {Card, Colors, P} from '../UI/index.js'
import prefersReducedMotion from '../../utils/PrefersReducedMotion.js'

export interface VerticalBarChartProps {
    width: number
    height: number
    data: {value: number, date: string, realDate?: string, top5?: {name: string, value: number, href?: string}[]}[]
    dataPrefix?: string
    dataSuffix?: string
    divergingData?: {value: number, date: string, realDate?: string}[]
    divergingYLabels?: {
        normal: string
        diverging: string
    }
    divergingDataPrefix?: string
    divergingDataSuffix?: string
    dataBarColor?: string
    divergingDataBarColor?: string
    maxYAxis?: number
    showGrid?: boolean
    animated?: boolean
    interval?: 'day' | 'week' | 'month' | 'route' // route is a special case where each bar represents a single route, not a date
    history?: any
}

/**
 * @param {VerticalBarChartProps} props
 *
 * @returns {React.JSX.Element} The rendered VerticalBarChart component
 */
export default function VerticalBarChart({width, height, data, dataPrefix = '', dataSuffix = '', divergingData, divergingDataPrefix = '', divergingDataSuffix = '', divergingYLabels, dataBarColor, divergingDataBarColor, maxYAxis, animated = !prefersReducedMotion, interval = 'day', history}: VerticalBarChartProps): React.JSX.Element {
    const tooltipRef = useRef<HTMLDivElement>()
    const [currentTop5, setCurrentTop5] = useState<VerticalBarChartProps['data'][0]['top5']>([])
    const margin = {top: 20, right: 40, bottom: 60, left: 80}
    const barSpacing = 10

    const combinedData = useMemo(() => {
        const merged = [...data, ...(divergingData || [])]
        if (interval === 'route') {
            merged.sort((a, b) => {
                const dateA = a.realDate ? moment(a.realDate, 'YYYY-MM-DD') : moment(a.date.split(' ').pop(), 'DD-MM')
                const dateB = b.realDate ? moment(b.realDate, 'YYYY-MM-DD') : moment(b.date.split(' ').pop(), 'DD-MM')

                if (dateA.isBefore(dateB)) {
                    return -1
                } else if (dateA.isAfter(dateB)) {
                    return 1
                } else {
                    const routeNameA = a.date.split(' ').slice(0, -1).join(' ')
                    const routeNameB = b.date.split(' ').slice(0, -1).join(' ')

                    return routeNameA.localeCompare(routeNameB)
                }
            })
        } else {
            merged.sort((a, b) => {
                return new Date(a.date).getTime() - new Date(b.date).getTime()
            })
        }
        return merged
    }, [data, divergingData, interval])

    const minimumX = interval === 'route' ? combinedData[0].date : interval === 'month' ? d3.min(combinedData, (d) => d.date) : d3.min(combinedData, (d) => new Date(d.date)) || 0
    let maximumX = interval === 'route' ? combinedData.at(-1).date : interval === 'month' ? d3.max(combinedData, (d) => d.date) : d3.max(combinedData, (d) => new Date(d.date)) || 0

    let tickInterval = {
        day: d3.utcDay,
        week: d3.utcWeek,
        month: d3.utcMonth,
        route: d3.utcDay
    }[interval]

    if (interval !== 'route') {
        const minAmount = 7
        const intervalCount = tickInterval.count(minimumX as Date, maximumX as Date)
        if (intervalCount < minAmount) {
            const newMax = moment(minimumX as Date).add(minAmount - 1, interval)
            maximumX = newMax.toDate()
        }
    }

    const barWidth = useMemo(() => {
        const length = (interval === 'route' || interval === 'month') ? combinedData.length : tickInterval.count(minimumX as Date, maximumX as Date) + 1
        const newWidth = (width - margin.left - margin.right) / length - barSpacing
        if (newWidth > 300) {
            return 300
        }

        if (newWidth < 10) {
            return 10
        }

        return newWidth
    }, [combinedData, interval, width])

    const svgRef = useRef(null)

    const dataIsEmpty = useMemo(() => combinedData.length === 0 || combinedData.every((entry) => entry.value === 0), [combinedData])

    useEffect(() => {
        if (svgRef.current) {
            const svg = d3.select(svgRef.current)
            const count = svg.selectAll('.bar').size()
            const duration = 750
            const delay = 50
            const totalDur = Math.min((duration + (count - 1) * delay), 1_000) // max duration of 1 second
            const bars = svg.selectAll('.bar')
            // const labels = bars.select((_, i, bars) => bars[i].nextElementSibling)

            if (animated) {
                const delayDurPerBar = totalDur / bars.size()

                // labels.attr('x', (_, i, labels) => {
                //     const el = labels[i]
                //     const value = el.getAttribute('data-value')

                //     const currentX = el.getAttribute('x')

                //     if (currentX) {
                //         return currentX
                //     }

                //     return xScale(value) - xScale(minimumX)
                // })

                bars
                    .attr('height', 0)
                    .attr('y', yScale(0))
                    .transition()
                    .duration(totalDur)
                    .delay((_, i) => i * delayDurPerBar)
                    .attr('height', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        const value = el.getAttribute('data-value')
                        if (!value) {
                            return null
                        }

                        return yScale(0) - yScale(parseFloat(value))
                    })
                    .attr('y', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        const value = el.getAttribute('data-value')
                        if (!value) {
                            return null
                        }

                        const isDivergent = el.getAttribute('data-is-divergent') === 'true'

                        return isDivergent ? yScale(0) : yScale(parseFloat(value))
                    })

                bars
                    .on('mouseover', (event) => {
                        const el = event.currentTarget
                        const elDate = el.getAttribute('data-date')
                        const top5 = data.find((d) => d.date === elDate)?.top5
                        if (top5) {
                            setCurrentTop5(top5)
                            tooltipRef.current!.style.visibility = 'visible'
                        }
                    })
                    .on('mousemove', (event) => {
                        tooltipRef.current!.style.top = `${event.offsetY + 10}px`
                        tooltipRef.current!.style.left = `${event.offsetX + 10}px`
                    })
                    .on('mouseout', () => {
                        tooltipRef.current!.style.visibility = 'hidden'
                    })

                d3.select(tooltipRef.current).on('mouseleave', () => {
                    tooltipRef.current!.style.visibility = 'hidden'
                })
            }
        }
    }, [combinedData, height])

    const xScale = useMemo(() => {
        if (interval === 'route' || interval === 'month') {
            return d3.scaleBand()
                .domain(combinedData.map((d) => d.date))
                .range([margin.left, width - margin.right])
        } else {
            return d3.scaleUtc()
                .domain([minimumX as Date | d3.NumberValue, maximumX as Date | d3.NumberValue])
                .range([margin.left, width - margin.right - barWidth])
        }
    }, [combinedData, width, interval, barWidth])

    const yScale = useMemo(() => {
        if (divergingData) {
            let max = dataIsEmpty ? 100 : Math.max(d3.max(divergingData, (d) => d.value) || 0, d3.max(data, (d) => d.value) || 0)
            if (maxYAxis && (max > maxYAxis)) {
                max = maxYAxis
            }
            return d3.scaleLinear()
                .domain([-Math.abs(max), max]).nice()
                .range([height - margin.bottom, margin.top])
        } else {
            return d3.scaleLinear()
                .domain([0, dataIsEmpty ? 100 : d3.max(data, (d) => d.value) || 0]).nice()
                .range([height - margin.bottom, margin.top])
        }
    }, [data, divergingData, height, dataIsEmpty])

    const maxTicks = Math.floor((width - margin.left - margin.right) / 40)

    let routeTickIndicesToKeep = []

    if (interval === 'route') {
        if (combinedData.length > maxTicks) {
            const stepCount = Math.floor(combinedData.length / maxTicks)
            let counter = 0
            for (let i = 0; i < combinedData.length; i++) {
                if (i === (counter + stepCount)) {
                    routeTickIndicesToKeep.push(i)

                    counter += stepCount
                }
            }
        }
    } else {
        while (tickInterval.count(minimumX as Date, maximumX as Date) > maxTicks) {
            if (interval === 'day') {
                tickInterval = d3.utcWeek
            } else if (interval === 'week') {
                tickInterval = d3.utcMonth
            } else {
                break
            }
        }
    }

    const xTicks = (interval === 'route' || interval === 'month') ?
        combinedData.map((d) => d.date) :
        (xScale as d3.ScaleTime<number, number>).ticks(tickInterval.every(1)!)

    const xTickFormat = {
        day: 'DD-MM',
        week: '[WK]W',
        month: 'MMM',
        route: '[RT]R'
    }[interval]

    const xTickElements = xTicks.map((tick: string | Date, index) => {
        let tSpans
        if (interval === 'route' && typeof tick === 'string') {
            if (combinedData.length > maxTicks && !routeTickIndicesToKeep.includes(index)) {
                return
            }

            const splitted = tick.split(' ')
            const date = splitted.pop()
            // const routeName = splitted.join(' ')
            tSpans = (
                <>
                    {/* <tspan x={0} dy={0}>{routeName}</tspan> */}
                    <tspan x={0} dy={12}>{date}</tspan>
                </>
            )
        }

        return (
            <g key={(interval === 'route' && typeof tick === 'string') ? `${tick.replaceAll(' ', '-')}-${index}` : typeof tick === 'string' ? tick : tick.getTime()} transform={`translate(${xScale(tick as string & (Date | d3.NumberValue)) + (barWidth / 2)}, ${height})`}>
                <text lengthAdjust='spacingAndGlyphs' style={{textAnchor: 'middle', fontSize: 16, transform: 'rotate(-65deg) translate(20px, 0px)'}}>{interval === 'route' ? tSpans : moment(tick).format(xTickFormat)}</text>
            </g>
        )
    }).filter((el) => el)

    const format = (value: number) => {
        return `${value > 0 ? dataPrefix : value === 0 ? '' : divergingDataPrefix}${Math.abs(value).toLocaleString('nl-NL')}${value > 0 ? dataSuffix : value === 0 ? '' : divergingDataSuffix}`
    }

    const [min, max] = yScale.domain()

    const step = Math.max(1, Math.ceil((max - min) / 5))

    const yTicks = divergingData ?
        yScale.ticks() :
        d3.range(
            Math.ceil(min),
            Math.floor(max) + 1,
            step
        )

    const yTickElements = yTicks.map((tick) => (
        <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
            <text x={margin.left - 24} dy='0.35em' textAnchor='end'>{format(tick)}</text>
            <line x1={margin.left} x2={width - margin.right} stroke={tick === 0 ? Colors.grey60 : Colors.grey40} strokeWidth={1} strokeOpacity={0.5} />
        </g>
    ))

    return (
        <div style={{position: 'relative'}}>
            {dataIsEmpty &&
                <Card style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', padding: 24}}>
                    Geen data gevonden in de geselecteerde periode.
                </Card>
            }
            <svg ref={svgRef} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <g>
                    <g>{yTickElements}</g>
                    {divergingYLabels && (
                        <>
                            <g transform={`translate(0, ${yScale(0) - 26})`}>
                                <foreignObject width={margin.left - 24} height={15} x={xScale(minimumX as string & (Date | d3.NumberValue)) - margin.left} dy='0.35em'>
                                    <P style={{fontSize: '10px', fontWeight: 700, textAlign: 'end', color: Colors.textDark}}>{divergingYLabels.normal}</P>
                                </foreignObject>
                            </g>
                            <g transform={`translate(0, ${yScale(0) + 10})`}>
                                <foreignObject width={margin.left - 24} height={24} x={xScale(minimumX as string & (Date | d3.NumberValue)) - margin.left} dy='0.35em'>
                                    <P style={{fontSize: '10px', fontWeight: 700, textAlign: 'center', lineHeight: 'normal', color: Colors.textDark}}>{divergingYLabels.diverging}</P>
                                </foreignObject>
                            </g>
                        </>
                    )}
                    <g>{xTickElements}</g>
                </g>
                <g>
                    {data.map((d, i) => {
                        const trimmedValue = maxYAxis ? Math.min(d.value, maxYAxis) : d.value

                        return (
                            <g key={`bar-${d.date}-${i}`}>
                                {currentTop5.length === 0 &&
                                    <title>{format(d.value)} - {interval === 'route' ? d.date : moment(d.date, 'YYYY-MM-DD').format('ddd DD MMM')}</title>
                                }
                                <rect
                                    className='bar'
                                    data-value={trimmedValue}
                                    data-date={d.date}
                                    x={xScale(((interval === 'route' || interval === 'month') ? d.date : new Date(d.date)) as string & (Date | d3.NumberValue))}
                                    // y={yScale(0)}
                                    width={barWidth}
                                    fill={dataBarColor || '#D1D1D1'}
                                    stroke='white'
                                    strokeWidth={yScale(d.value) > 1 ? 1 : 0}
                                />
                            </g>
                        )
                    })}
                </g>
                {divergingData && divergingData.length > 0 &&
                    <g>
                        {divergingData.map((d, i) => {
                            const trimmedValue = maxYAxis ? Math.min(d.value, maxYAxis) : d.value

                            return (
                                <g key={`${d.date}-${i}-divergent`}>
                                    {currentTop5.length === 0 &&
                                        <title>{format(-d.value)} - {interval === 'route' ? d.date : moment(d.date, 'YYYY-MM-DD').format('ddd DD MMM')}</title>
                                    }
                                    <rect
                                        className='bar'
                                        data-value={trimmedValue}
                                        data-is-divergent
                                        x={xScale(((interval === 'route' || interval === 'month') ? d.date : new Date(d.date)) as string & (Date | d3.NumberValue))}
                                        // y={yScale(0)}
                                        width={barWidth}
                                        fill={divergingDataBarColor || '#B7D2E9'}
                                        stroke='white'
                                        strokeWidth={yScale(d.value) > 1 ? 1 : 0}
                                    />
                                </g>
                            )
                        })}
                    </g>
                }
            </svg>
            {/* Tooltip */}
            <div ref={tooltipRef} style={{width: 150, position: 'absolute', visibility: 'hidden', backgroundColor: 'white', padding: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', borderRadius: 4}}>
                <P style={{fontSize: 12, fontWeight: 700, marginBottom: 6}}>Top 5</P>
                {currentTop5.map((item) => (
                    <P
                        key={item.name}
                        onClick={() => {
                            history.push(item.href || '#')
                        }}
                        style={{fontSize: 12, margin: 0, cursor: 'pointer'}}
                    >{item.name}: {item.value}
                    </P>
                ))}
            </div>
        </div>
    )
}
